import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal, MaskedLine, Overline, FadeIn } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000&auto=format&fit=crop",
  holdings: "https://images.unsplash.com/photo-1787539386490-4eeca293c066?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format&fit=crop",
  clothing: "https://images.unsplash.com/photo-1603189343302-e603f7add05a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format&fit=crop",
  business: "https://images.unsplash.com/photo-1625690303837-654c9666d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format&fit=crop",
  media: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format&fit=crop",
};

const divisions = [
  {
    num: "01",
    title: "ADD VALUE Holdings",
    desc: "The parent company — an owner-led structure developing brands, products, and opportunities across multiple industries.",
    to: "/about",
    img: IMAGES.holdings,
    span: "lg:col-span-7",
  },
  {
    num: "02",
    title: "ADD VALUE Clothing",
    desc: "The company's luxury apparel division. Adding value through what you wear.",
    to: "/clothing",
    img: IMAGES.clothing,
    span: "lg:col-span-5",
  },
  {
    num: "03",
    title: "Business & Ventures",
    desc: "Business development, brand development, e-commerce, and the partnerships that carry them forward.",
    to: "/business",
    img: IMAGES.business,
    span: "lg:col-span-5",
  },
  {
    num: "04",
    title: "Media & Content",
    desc: "Video, digital projects, and AI-assisted media now in development under the ADD VALUE name.",
    to: "/media",
    img: IMAGES.media,
    span: "lg:col-span-7",
  },
];

const pillars = [
  { n: "I", t: "Vision", d: "See what could exist before it does." },
  { n: "II", t: "Action", d: "Move deliberately. Build with intention." },
  { n: "III", t: "Relationships", d: "Value grows between people, never in isolation." },
  { n: "IV", t: "Consistency", d: "Show up. Refine. Repeat. Endure." },
];

const scrollToDivisions = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo("#divisions", { offset: -70, duration: 1.4 });
  } else {
    document.getElementById("divisions")?.scrollIntoView({ behavior: "smooth" });
  }
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    document.title = "ADD VALUE HOLDINGS LLC — Creating. Building. Adding Value.";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex h-screen min-h-[640px] items-end overflow-hidden"
      >
        <motion.img
          src={IMAGES.hero}
          alt="Luxury architecture — ADD VALUE HOLDINGS LLC"
          style={{ y: imgY }}
          className="absolute inset-0 h-[115%] w-full scale-110 object-cover opacity-50 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/40 to-[#0A0A0B]/30" />
        <p className="text-outline pointer-events-none absolute -right-6 top-24 hidden select-none font-serif text-[11rem] uppercase leading-none tracking-tight lg:block">
          Value
        </p>

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-7xl px-6 pb-32 sm:px-8 sm:pb-40 lg:px-12"
        >
          <FadeIn>
            <Overline data-testid="hero-overline">ADD VALUE HOLDINGS LLC</Overline>
          </FadeIn>
          <h1 className="mt-8 font-serif text-4xl uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
            <MaskedLine delay={0.2}>Creating.</MaskedLine>
            <MaskedLine delay={0.34}>Building.</MaskedLine>
            <MaskedLine delay={0.48}>
              Adding <span className="italic text-[#D4AF37]">Value.</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.75} className="mt-8 max-w-xl">
            <p data-testid="hero-subtext" className="text-sm font-light leading-relaxed text-stone-300 sm:text-base">
              ADD VALUE HOLDINGS LLC is a diversified company focused on
              building brands, developing opportunities, creating products, and
              developing ventures designed to add value.
            </p>
          </FadeIn>
          <FadeIn delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-explore-button"
              onClick={scrollToDivisions}
              className="group inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37]"
            >
              Explore ADD VALUE
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <Link
              data-testid="hero-contact-button"
              to="/contact"
              className="inline-flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              Contact Us
            </Link>
          </FadeIn>
        </motion.div>

        <div
          data-testid="hero-divisions-strip"
          className="absolute inset-x-0 bottom-0 hidden border-t border-white/10 bg-[#0A0A0B]/40 backdrop-blur-md sm:block"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-8 lg:px-12">
            {["Holdings", "Clothing", "Business & Ventures", "Media"].map((d, i) => (
              <span
                key={d}
                className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400"
              >
                <span className="text-[#D4AF37]/70">0{i + 1}</span>
                {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Overline>The Company</Overline>
            <h2 className="mt-8 font-serif text-2xl leading-snug tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              One philosophy, many ventures. ADD VALUE HOLDINGS LLC is the
              parent company behind a growing family of brands — built to make
              everything it touches{" "}
              <span className="italic text-[#D4AF37]">more valuable</span> than
              it was before.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-col justify-end lg:col-span-5">
            <p className="text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              From luxury apparel to business development and digital media,
              every division of ADD VALUE is built on the same foundation:
              vision, action, relationships, and consistency. The company is
              owner-led, deliberate, and at the beginning of a long story.
            </p>
            <Link
              data-testid="intro-about-link"
              to="/about"
              className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]"
            >
              Our Philosophy
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DIVISIONS */}
      <section id="divisions" className="mx-auto max-w-7xl px-6 pb-24 sm:px-8 lg:px-12 lg:pb-32">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>The Portfolio</Overline>
            <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Divisions & Ventures
            </h2>
          </div>
          <p className="max-w-sm text-sm font-light text-stone-500">
            A growing structure of brands and projects — each one deliberate,
            each one built to add value.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {divisions.map((d, i) => (
            <Reveal key={d.num} delay={0.08 * i} className={d.span}>
              <Link
                to={d.to}
                data-testid={`division-card-${d.num}`}
                className="group relative block overflow-hidden border border-[#222226] bg-[#121214] transition-colors duration-500 hover:border-[#D4AF37]/40"
              >
                <div className="relative h-80 overflow-hidden sm:h-96 lg:h-[26rem]">
                  <img
                    src={d.img}
                    alt={d.title}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-70 grayscale transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]">
                      {d.num}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl text-white sm:text-3xl">
                      {d.title}
                    </h3>
                    <p className="mt-3 max-w-md text-sm font-light text-stone-400">
                      {d.desc}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-300 transition-colors duration-300 group-hover:text-[#D4AF37]">
                      Explore
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}

          <Reveal delay={0.1} className="lg:col-span-12">
            <Link
              to="/contact"
              data-testid="division-card-05"
              className="group flex flex-wrap items-center justify-between gap-6 border border-[#222226] bg-[#121214] p-8 transition-colors duration-500 hover:border-[#D4AF37]/40 lg:p-12"
            >
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]">
                  05
                </p>
                <h3 className="mt-3 font-serif text-2xl text-white sm:text-3xl">
                  Future Ventures
                </h3>
                <p className="mt-3 max-w-xl text-sm font-light text-stone-400">
                  What comes next — new brands, products, and opportunities
                  currently taking shape inside the company.
                </p>
              </div>
              <span className="flex h-16 w-16 items-center justify-center border border-[#2a2a2e] text-stone-300 transition-colors duration-300 group-hover:border-[#D4AF37] group-hover:text-[#D4AF37]">
                <ArrowUpRight className="h-6 w-6 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-t border-[#1c1c1f] bg-[#0d0d0f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <Overline>Our Philosophy</Overline>
            <h2 className="mt-8 max-w-3xl font-serif text-2xl leading-snug tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Value is created through vision, action, relationships, and
              consistency.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px border border-[#222226] bg-[#222226] sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p, i) => (
              <Reveal key={p.n} delay={0.08 * i} className="bg-[#0d0d0f]">
                <div
                  data-testid={`pillar-${p.n}`}
                  className="group h-full p-8 transition-colors duration-500 hover:bg-[#121214] lg:p-10"
                >
                  <p className="font-serif text-3xl italic text-[#D4AF37]/80">
                    {p.n}
                  </p>
                  <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-white">
                    {p.t}
                  </h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-stone-500">
                    {p.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
            <div>
              <Overline>Work With Us</Overline>
              <h2 className="mt-8 font-serif text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl">
                Build something of{" "}
                <span className="italic text-[#D4AF37]">value</span>.
              </h2>
            </div>
            <Link
              data-testid="cta-contact-button"
              to="/contact"
              className="group inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37]"
            >
              Start the Conversation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
